import { useId, type CSSProperties, type ReactNode } from 'react';
import { T } from '@territory/tokens';
import { control, DENSE_PHONE, font, radius, space, TOUCH_MIN, weight } from '../scale.js';
import { VisuallyHidden } from '../internal/VisuallyHidden.js';
import { consoleRole } from '../internal/consoleRoles.js';
import { Icon } from '../Icon/Icon.js';

/*
 * The shell TextField and Select share: label, 16px control, hint/error line and their id wiring. Kept next to
 * TextField rather than in internal/ until a third field needs it.
 */

export { renderIconSlot, type IconSlot } from '../internal/iconSlot.js';

export type FieldSize = 'sm' | 'md';
/* 'console': the owner console field (legacy .finput), from the products.consoles field roles.
   'console-inline': the owner Enterprise view inline field (legacy `IS` style), unstyled browser font.
   'panel': the owner panel form field (legacy .pmfld / .otbfld in the Team and Outbound drawers): the console control
   in the body font at 14px under a mono uppercase caps label. */
export type FieldVariant = 'default' | 'console' | 'console-inline' | 'panel';

/* verbatim wedge, off-scale */
const MULTILINE_MIN = 96;
export const ADORN = 20;
const ERROR_ICON = 14;
/* Input inset that clears a 20px adornment: its inset, the glyph, then a gap. */
export const ADORN_PAD = space[3] + ADORN + space[2];

export const FIELD_CSS = `[data-rv-field-control]::placeholder { color: ${T.labelTer}; opacity: 1; }
[data-rv-field-control]:focus-visible { outline: 2px solid ${T.accent}; outline-offset: 2px; border-color: ${T.accent} !important; box-shadow: 0 0 0 3px ${T.accentTint}; }
[data-rv-field-control][aria-invalid="true"]:focus-visible { border-color: ${T.red} !important; }
[data-rv-field-control]:focus:not(:focus-visible) { outline: none; }
[data-rv-field-control]:disabled { cursor: not-allowed; }`;

/* verbatim legacy owner .finput, off-scale */
const CONSOLE_FIELD_H = 36;
const CONSOLE_FIELD_PAD = '8px 11px';
const CONSOLE_FIELD_R = 9;
const CONSOLE_FIELD_MS = 150;
/* verbatim legacy admin inline select (Select `dense`) (style="padding:4px 8px;min-height:30px;font-size:11px"), off-scale */
const CONSOLE_DENSE_H = 30;
const CONSOLE_DENSE_PAD = '4px 8px';
const CONSOLE_DENSE_SIZE = '11px';
/* legacy select.finput at 560px and below: 44px and 16px, so iOS does not zoom on focus */
const CONSOLE_SELECT_PHONE = '@media (max-width: 560px)';
/* verbatim legacy owner .pmfld, off-scale */
const PANEL_FIELD_FONT = 14;
const PANEL_MULTILINE_MIN = 84;
const PANEL_MULTILINE_LEADING = 1.45;
const PANEL_LABEL_SIZE = 11;
const PANEL_LABEL_TRACK = 1;
const PANEL_LABEL_GAP = 5;

const CONSOLE_FIELD = '[data-rv-field-control][data-rv-look="console"]';
/* Legacy dark .finput sets outline none and marks focus by its border only; the library keeps its 2px focus outline
   (CONVENTIONS.md), in the console field-focus-outline hue. Light legacy draws the same outline in its own hue. */
export const FIELD_CONSOLE_CSS = `${CONSOLE_FIELD}::placeholder { color: ${consoleRole('field-placeholder', T.labelTer)}; opacity: 1; }
${CONSOLE_FIELD}:focus-visible { outline: 2px solid ${consoleRole('field-focus-outline', T.accent)}; outline-offset: 2px; border-color: ${consoleRole('field-focus-edge', T.accentTint)} !important; box-shadow: ${consoleRole('field-focus-ring', 'none')}; }
${CONSOLE_FIELD}[aria-invalid="true"]:focus-visible { border-color: ${T.red} !important; }
${DENSE_PHONE} { ${CONSOLE_FIELD} { min-height: ${TOUCH_MIN}px; } }
${CONSOLE_SELECT_PHONE} { select${CONSOLE_FIELD} { font-size: ${font.headline}px; } }`;

/* verbatim legacy owner .cmdjin textarea / .cmdjsearch / .cmdjfilter, off-scale */
const COMPACT_H = 32;
const COMPACT_MULTI_H = 64;

/* Legacy .finput: mono 13px for text, the body font for select.finput, no chevron, 36px from the min-height.
   `compact` is variant 'console' with size 'sm', the journal field family (legacy .cmdjin textarea, .cmdjsearch,
   .cmdjfilter: 32px, second glass fill): mono 12.5px search, mono 12px select, body 13.5px textarea. */
export function consoleControlStyle(opts: { error: boolean; select?: boolean; multiline?: boolean; compact?: boolean; dense?: boolean }): CSSProperties {
  if (opts.compact)
    return {
      display: 'block',
      width: '100%',
      boxSizing: 'border-box',
      margin: 0,
      height: opts.multiline ? undefined : COMPACT_H,
      minHeight: opts.multiline ? COMPACT_MULTI_H : undefined,
      padding: opts.multiline ? '9px 11px' : opts.select ? '6px 8px' : '6px 10px',
      borderRadius: opts.multiline ? 10 : 9,
      background: T.surfaceAlt,
      border: `1px solid ${opts.error ? T.red : T.separator}`,
      color: opts.select ? T.labelSec : T.label,
      caretColor: T.accent,
      fontFamily: opts.multiline ? T.font : T.mono,
      fontSize: opts.multiline ? 13.5 : opts.select ? font.caption : 12.5,
      fontWeight: weight.regular,
      lineHeight: opts.multiline ? 1.5 : 'normal',
      transition: `border-color ${CONSOLE_FIELD_MS}ms ${T.ease}`,
      resize: opts.multiline ? 'vertical' : undefined,
    };
  return {
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
    margin: 0,
    minHeight: opts.multiline ? MULTILINE_MIN : opts.dense ? CONSOLE_DENSE_H : CONSOLE_FIELD_H,
    padding: opts.dense ? CONSOLE_DENSE_PAD : CONSOLE_FIELD_PAD,
    borderRadius: CONSOLE_FIELD_R,
    background: consoleRole('field-fill', T.surface),
    border: `1px solid ${opts.error ? T.red : consoleRole('field-edge', T.separator)}`,
    color: T.label,
    caretColor: T.accent,
    fontFamily: opts.select ? T.font : T.mono,
    fontSize: opts.dense ? consoleRole('field-compact-size', CONSOLE_DENSE_SIZE) : font.footnote,
    fontWeight: weight.regular,
    lineHeight: 'normal',
    transition: `border-color ${CONSOLE_FIELD_MS}ms ${T.ease}`,
    resize: opts.multiline ? 'vertical' : undefined,
  };
}

/* verbatim legacy owner Enterprise `IS` inline style, off-scale */
const INLINE_FIELD_PAD = '8px 10px';

const INLINE_FIELD = '[data-rv-field-control][data-rv-look="console-inline"]';
/* Legacy sets no focus rule here, so the base focus ring (FIELD_CSS) applies; only the placeholder ink is a role. On
   phones the field grows to the 44px touch minimum, as the console field does (legacy stays 33px). */
export const FIELD_INLINE_CSS = `${INLINE_FIELD}::placeholder { color: ${consoleRole('field-placeholder', T.labelTer)}; opacity: 1; }
${DENSE_PHONE} { ${INLINE_FIELD} { min-height: ${TOUCH_MIN}px; } }`;

/* Legacy Enterprise `IS`: --card fill, 1px --line edge, radius 8, padding 8px 10px, 13px, no min height. Legacy sets no
   font family on inputs and selects, so they keep the browser control font; its textareas inherit the page font.
   A select keeps the native chevron (legacy sets no appearance). */
export function consoleInlineControlStyle(opts: { error: boolean; multiline?: boolean }): CSSProperties {
  return {
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
    margin: 0,
    padding: INLINE_FIELD_PAD,
    borderRadius: radius.sm,
    background: consoleRole('inline-field-fill', T.surface),
    border: `1px solid ${opts.error ? T.red : T.separator}`,
    color: T.label,
    fontFamily: opts.multiline ? 'inherit' : undefined,
    fontSize: font.footnote,
    lineHeight: 'normal',
    resize: opts.multiline ? 'vertical' : undefined,
  };
}

/* Legacy .pmfld .finput: the console control, body font 14px; a textarea is 84px tall at 1.45 leading. */
export function panelControlStyle(opts: { error: boolean; select?: boolean; multiline?: boolean }): CSSProperties {
  return {
    ...consoleControlStyle(opts),
    fontFamily: T.font,
    fontSize: PANEL_FIELD_FONT,
    ...(opts.multiline ? { minHeight: PANEL_MULTILINE_MIN, lineHeight: PANEL_MULTILINE_LEADING } : null),
  };
}

export interface FieldIds {
  id: string;
  hintId: string;
  errorId: string;
}

export function useFieldIds(id?: string): FieldIds {
  const auto = useId();
  const base = id ?? `rv-field-${auto}`;
  return { id: base, hintId: `${base}-hint`, errorId: `${base}-error` };
}

/* The consumer's own aria-describedby is kept first; the message line is appended. */
export function describedBy(ids: FieldIds, own: string | undefined, hint: ReactNode, error: ReactNode): string | undefined {
  const msg = error ? ids.errorId : hint ? ids.hintId : undefined;
  return [own, msg].filter(Boolean).join(' ') || undefined;
}

export function rootStyle(disabled: boolean | undefined, style: CSSProperties | undefined): CSSProperties {
  return {
    display: 'flex',
    flexDirection: 'column',
    fontFamily: T.font,
    opacity: disabled ? 0.4 : undefined,
    ...style,
  };
}

export function controlStyle(opts: { size: FieldSize; error: boolean; multiline?: boolean; mono?: boolean }): CSSProperties {
  return {
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
    margin: 0,
    minHeight: opts.multiline ? MULTILINE_MIN : opts.size === 'sm' ? TOUCH_MIN : control.field,
    padding: opts.multiline ? `${space[3]}px ${space[4]}px` : `0 ${space[4]}px`,
    borderRadius: radius.lg,
    background: T.surface,
    border: `1px solid ${opts.error ? T.red : T.cardBorder}`,
    color: T.label,
    caretColor: T.accent,
    fontFamily: opts.mono ? T.mono : T.font,
    /* 16px or iOS Safari zooms the page on focus. */
    fontSize: font.headline,
    fontWeight: weight.regular,
    lineHeight: 1.4,
    resize: opts.multiline ? 'vertical' : undefined,
  };
}

export function FieldLabel({
  htmlFor,
  id,
  hidden,
  required,
  caps,
  children,
}: {
  htmlFor: string;
  id?: string;
  hidden?: boolean;
  required?: boolean;
  /* Panel field label (legacy .pmfld label): mono 11px uppercase, 1px tracking. */
  caps?: boolean;
  children: ReactNode;
}) {
  const marker = required ? (
    <span aria-hidden="true" style={{ color: T.red }}>
      {' *'}
    </span>
  ) : null;
  if (hidden) {
    return (
      <label htmlFor={htmlFor} id={id}>
        <VisuallyHidden>{children}</VisuallyHidden>
      </label>
    );
  }
  return (
    <label
      htmlFor={htmlFor}
      id={id}
      style={
        caps
          ? {
              display: 'block',
              marginBottom: PANEL_LABEL_GAP,
              fontFamily: T.mono,
              fontSize: PANEL_LABEL_SIZE,
              fontWeight: weight.regular,
              letterSpacing: PANEL_LABEL_TRACK,
              textTransform: 'uppercase',
              color: T.labelSec,
            }
          : {
              display: 'block',
              marginBottom: space[1],
              fontSize: font.footnote,
              fontWeight: weight.semibold,
              color: T.labelSec,
            }
      }
    >
      {children}
      {marker}
    </label>
  );
}

/* caps: the panel field hint (legacy .pmfld .pmhint): 12px, 5px under the control. */
export function FieldMessage({ ids, hint, error, caps }: { ids: FieldIds; hint: ReactNode; error: ReactNode; caps?: boolean }) {
  if (error) {
    return (
      <div
        id={ids.errorId}
        data-rv-field-error=""
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: space[1],
          marginTop: space[1],
          fontSize: font.footnote,
          lineHeight: `${space[5]}px`,
          color: T.red,
        }}
      >
        <Icon name="alert" size={ERROR_ICON} style={{ flexShrink: 0, marginTop: (space[5] - ERROR_ICON) / 2 }} />
        <span>{error}</span>
      </div>
    );
  }
  if (!hint) return null;
  return (
    <div
      id={ids.hintId}
      data-rv-field-hint=""
      style={{ marginTop: caps ? PANEL_LABEL_GAP : space[1], fontSize: caps ? font.caption : font.footnote, color: T.labelSec }}
    >
      {hint}
    </div>
  );
}
